import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Notepad, PencilSimple, Trash } from "@phosphor-icons/react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { formatDateShort } from "@/lib/format";
import { DOCUMENT_TYPE_LABELS, type DocumentSummary } from "@/types";

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1",
        isActive
          ? "bg-emerald-50 text-emerald-700 ring-emerald-500/20"
          : "bg-stone-100 text-stone-500 ring-stone-500/20",
      )}
    >
      <span className={cn("size-1.5 rounded-full", isActive ? "bg-emerald-500" : "bg-stone-400")} />
      {isActive ? "Aktivan" : "Neaktivan"}
    </span>
  );
}

function TypeBadge({ type }: { type: DocumentSummary["type"] }) {
  return (
    <span className="inline-flex items-center rounded-full bg-primary/8 px-2.5 py-1 text-xs font-medium text-primary">
      {DOCUMENT_TYPE_LABELS[type]}
    </span>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <div className={cn("flex shrink-0 items-center justify-center rounded-xl bg-primary/10", className)}>
      <Notepad className="size-[45%] text-primary" />
    </div>
  );
}

function RowActions({
  document,
  onDelete,
}: {
  document: DocumentSummary;
  onDelete: (id: string) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="inline-flex items-center gap-0.5">
      <Link
        to="/dashboard/admin/documents/$documentId"
        params={{ documentId: document.id }}
        aria-label={`Uredi dokument: ${document.title}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex size-7 items-center justify-center rounded-lg text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
      >
        <PencilSimple className="size-4" />
      </Link>
      <button
        type="button"
        aria-label={`Obriši dokument: ${document.title}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setConfirmOpen(true);
        }}
        className="inline-flex size-7 items-center justify-center rounded-lg text-muted-foreground/60 transition-colors hover:bg-rose-50 hover:text-rose-600"
      >
        <Trash className="size-4" />
      </button>

      <ConfirmDialog
        open={confirmOpen}
        title="Obriši dokument"
        description={`Da li ste sigurni da želite obrisati "${document.title}"? Ova radnja se ne može poništiti.`}
        confirmLabel="Obriši"
        tone="destructive"
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          onDelete(document.id);
          setConfirmOpen(false);
        }}
      />
    </div>
  );
}

/* ─── Desktop table + mobile cards ─── */

export function DocumentsTable({
  documents,
  onDelete,
}: {
  documents: DocumentSummary[];
  onDelete: (id: string) => void;
}) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm lg:block">
        <table className="w-full table-fixed text-[13px]">
          <colgroup>
            <col />
            <col className="w-[180px]" />
            <col className="w-[100px]" />
            <col className="w-[80px]" />
            <col className="w-[140px]" />
            <col className="w-[88px]" />
          </colgroup>
          <thead>
            <tr className="bg-muted/40">
              <Th>Naziv</Th>
              <Th>Tip</Th>
              <Th>Status</Th>
              <Th align="right">Verzija</Th>
              <Th align="right">Izmijenjeno</Th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {documents.map((doc, i) => (
              <tr
                key={doc.id}
                className={cn(
                  "group transition-colors hover:bg-primary/[0.03]",
                  i !== documents.length - 1 && "border-b border-border/40",
                )}
              >
                <td className="px-4 py-3.5">
                  <Link
                    to="/dashboard/admin/documents/$documentId"
                    params={{ documentId: doc.id }}
                    className="flex items-center gap-3"
                  >
                    <DocumentIcon className="size-9" />
                    <div className="min-w-0">
                      <div className="truncate font-medium leading-tight text-foreground">
                        {doc.title}
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                        {doc.slug}
                      </div>
                    </div>
                  </Link>
                </td>
                <td className="px-3 py-3.5">
                  <TypeBadge type={doc.type} />
                </td>
                <td className="px-3 py-3.5">
                  <StatusBadge isActive={doc.isActive} />
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-muted-foreground">
                  v{doc.version}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-muted-foreground">
                  {formatDateShort(doc.updatedAt)}
                </td>
                <td className="py-3.5 pl-1 pr-4 text-right">
                  <RowActions document={doc} onDelete={onDelete} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm"
          >
            <Link
              to="/dashboard/admin/documents/$documentId"
              params={{ documentId: doc.id }}
              className="flex items-start justify-between gap-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                <DocumentIcon className="size-11" />
                <div className="min-w-0">
                  <div
                    className="truncate text-sm font-semibold text-foreground"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {doc.title}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">{doc.slug}</div>
                </div>
              </div>
              <StatusBadge isActive={doc.isActive} />
            </Link>

            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border/40 pt-3 text-xs">
              <MobileCell label="Tip" value={DOCUMENT_TYPE_LABELS[doc.type]} />
              <MobileCell label="Verzija" value={`v${doc.version}`} muted />
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-border/40 pt-3 text-xs text-muted-foreground">
              <span className="tabular-nums">{formatDateShort(doc.updatedAt)}</span>
              <RowActions document={doc} onDelete={onDelete} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function MobileCell({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 truncate",
          muted ? "text-muted-foreground" : "font-semibold text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={cn(
        "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground first:px-4",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}
